import React, { useEffect, useState, useCallback } from 'react'
import omit from 'lodash/omit'
import pick from 'lodash/pick'
import map from 'lodash/map'
import find from 'lodash/find'
import reject from 'lodash/reject'
import sort from 'lodash/sortBy'

import UserForm from './form'
import UserTable from './table'
import LogTable from './LogTable'
import SecurityAdministrationLayout from '../../commons/SecurityAdministrationLayout'

import { registerAction } from 'actions/SecurityAdminActions'
import { getFromApi, postToApi, putToApi } from 'actions'
import { OPERACIONES_USUARIO, VALUES } from 'utils/constants'
import { buildFiltersDescriptors } from '../../commons/SearchFilters'
import { useStateValue } from 'context/store'
import { useApiRequest, handleErrorMessage } from 'utils/customHooks/useApiRequest'
import { useGetUserSettings } from 'utils/customHooks/useGetUserSettings'
import { useDocumentTypeList } from 'utils/customHooks/useDocTypesList'
import { closeSnackbarAction, openSnackBarAction } from 'context/reducers/openSnackbarReducer'
import { closeSpinnerAction, openSpinnerAction } from 'context/reducers/openSpinnerReducer'
import { useHasOperation } from 'hooks/useHasOperation'

const EXTERNAL_ADMIN_DOC_TYPES = [
   // TODO: reemplazar con el del VALUES y filtrar
   { value: 'CC', label: 'Cédula de Ciudadanía' },
   { value: 'CE', label: 'Cédula de Extranjería' }
]

const INITIAL_USER_FORM_STATE = {
   id: '',
   name: '',
   lastName: '',
   documentNumber: '',
   documentType: '',
   cellphone: '',
   email: '',
   telephone: '',
   adminUser: false,
   status: '',
   roles: [],
   groups: [],
   entityNit: '',
   entityName: '',
   thirdPartyCode: ''
}

const { STATUS_OPTIONS } = VALUES

const TITLE = {
   [VALUES.FORM_BEHAVIOR.REGISTER]: 'Registrar Usuario',
   [VALUES.FORM_BEHAVIOR.SEARCH]: 'Consultar Usuario',
   [VALUES.FORM_BEHAVIOR.EDIT]: 'Editar Usuario'
}

const User = ({ isSearch }) => {
   const [availableRoles, setAvailableRoles] = useState([])
   const [renewForm, setRenewForm] = useState(0)
   const [isShowingPopover, setPopoverProp] = useState(true)
   const [ipDevice, setIpDevice] = useState('')
   const [groups, setGroups] = useState([])
   const [thirdTypes, setThirdTypes] = useState([])

   const [formData, setFormData] = useState(INITIAL_USER_FORM_STATE)
   const [formBehavior, setFormBehavior] = useState(
      isSearch ? VALUES.FORM_BEHAVIOR.SEARCH : VALUES.FORM_BEHAVIOR.REGISTER
   )
   const [searchFilters, setSearchFilters] = useState({})
   const [logUserSelected, setLogUserSelected] = useState()
   const [tableData, setTableData] = useState(VALUES.TABLE_DATA_INTIAL_STATE)
   const [paginationParams, setPaginationParams] = useState(VALUES.PAGING_ORDER_INITIAL_STATE)
   const [options, setOptions] = useState([...STATUS_OPTIONS])

   const [{ sessionUser }, dispatch] = useStateValue()
   const { isInternalAdmin, isInternalSuperAdmin, isInternalUser } = useGetUserSettings()
   const handleSelectUserAudit = useCallback(rowData => setLogUserSelected(rowData?.id), [])
   const { docTypeList, testDocLengthValidation } = useDocumentTypeList()

   const seeLogPermission = useHasOperation(OPERACIONES_USUARIO.VER_TRAZA)
   const editPermission = useHasOperation(OPERACIONES_USUARIO.EDITAR)

   const documentTypes = docTypeList
   const userSettings = sessionUser?.settings

   // ---------------- Clean Form -----------------
   function clean() {
      setPopoverProp(true)
      setTableData(VALUES.TABLE_DATA_INTIAL_STATE)
      setPaginationParams(VALUES.PAGING_ORDER_INITIAL_STATE)
      setSearchFilters({})
      cleanForm()
      logUserSelected && setLogUserSelected()
   }

   function cleanForm() {
      setFormData({
         ...INITIAL_USER_FORM_STATE,
         entityNit: !isInternalAdmin && !isSearch ? userSettings?.entity?.nit : '',
         entityName: !isInternalAdmin && !isSearch ? userSettings?.entity?.name : ''
      })
      setFormBehavior(isSearch ? VALUES.FORM_BEHAVIOR.SEARCH : VALUES.FORM_BEHAVIOR.REGISTER)
      setRenewForm(old => old + 1)
   }

   // eslint-disable-line react-hooks/exhaustive-deps

   // ----------------- Fetch all available Roles -------------------
   async function _getAvailableRoles() {
      const getRolesUrl = `${process.env.REACT_APP_USERS_API}/users/findAllowedRolesToAssign`
      const rolesResponse = await getFromApi(getRolesUrl)
      if (rolesResponse?.length) {
         setAvailableRoles(rolesResponse)
      }
   }

   const getAvailableRoles = useApiRequest(_getAvailableRoles, 'Usuarios: Consultar Roles')

   // eslint-disable-line react-hooks/exhaustive-deps

   // ---------------- Fetch entities by Name ---------------

   async function fetchEntitiesByName(entityName) {
      // NOTA: No aplica useApiRequest por el return de la data
      try {
         dispatch(openSpinnerAction())
         dispatch(closeSnackbarAction())
         const url = `${process.env.REACT_APP_USERS_API}/entities?name=${entityName}`
         const searchedEntities = await getFromApi(url)
         if (!searchedEntities.length) {
            dispatch(openSnackBarAction(`No se encontraron resultados para "${entityName}".`, 'warning'))
         }
         return searchedEntities
      } catch (error) {
         dispatch(openSnackBarAction(`${handleErrorMessage(error)} (Usuarios: Consulta Entidades)`))
      } finally {
         dispatch(closeSpinnerAction())
      }
   }

   // ---------------- Search Users ------------------
   function getFiltersDescriptors(filters) {
      // Lista blanca: solo campos primitivos seguros para renderizar como chips
      const safeFilters = pick(filters, [
         'documentType', 'documentNumber', 'name', 'lastName',
         'email', 'status', 'adminUser', 'entityNit', 'entityName'
      ])
      return buildFiltersDescriptors(options)(safeFilters) || []
   }

   async function removeSearchFilter(filter) {
      const remainingFilters = omit(searchFilters, [filter.filterName])
      await searchUsers(remainingFilters, VALUES.PAGING_ORDER_INITIAL_STATE)
   }

   async function _searchUsers(externalFilters, pagingFromTableParams) {
      const filters = externalFilters ? externalFilters : searchFilters
      const paginationFilters = {
         ...paginationParams,
         ...pagingFromTableParams
      }
      setSearchFilters(filters)
      setPaginationParams(paginationFilters)
      const data = {}
      if (filters.documentType) {
         data.documentType = filters.documentType
      }
      if (filters.documentNumber) {
         data.documentNumber = filters.documentNumber
      }
      if (filters.name) {
         data.name = filters.name
      }
      if (filters.documentType !== 'NI' && filters.lastName) {
         data.lastName = filters.lastName
      }
      if (filters.email) {
         data.email = filters.email
      }
      if (filters.status) {
         data.status = filters.status
      }
      if (filters.adminUser) {
         data.adminUser = filters.adminUser
      }
      if (filters.entityNit) {
         data.entityNit = filters.entityNit
      }

      const payload = { ...data, ...paginationFilters }
      if (!isInternalUser) payload.entityNit = userSettings?.entity?.nit
      const url = `${process.env.REACT_APP_USERS_API}/users/search`
      const searchedUsers = await getFromApi(url, payload)
      searchedUsers?.data?.forEach(user => {
         user.roleList = user?.thirdParty?.roleList?.map(rol => ({
            id: rol.id,
            name: rol.name
         }))
      })
      searchedUsers.nitOnly = filters.documentType === 'NI'
      setTableData(searchedUsers)
      setFormData(INITIAL_USER_FORM_STATE)
   }

   const searchUsers = useApiRequest(_searchUsers, 'Usuarios: Consultar')

   // ------------- Register Users ---------------
   async function _registerUser() {
      if (testDocLengthValidation(formData.documentType, formData.documentNumber)) return
      const payload = {
         documentType: formData.documentType,
         documentNumber: formData.documentNumber,
         name: formData.name,
         email: formData.email,
         cellphone: formData.cellphone,
         roles: formData.roles.map(role => role.id),
         entityNit: formData.entityNit,
         entityName: formData.entityName,
         thirdPartyCode: formData.thirdPartyCode?.code
      }
      if (formData.documentType !== 'NI') payload.lastName = formData.lastName
      if (formData.telephone) payload.telephone = formData.telephone
      if (isInternalSuperAdmin) payload.adminUser = formData.adminUser

      const url = `${process.env.REACT_APP_USERS_API}/validation/users?ip=${ipDevice}`
      const response = await registerAction(url, payload, cleanForm, setTableData, dispatch)
      return response
   }

   const registerUser = useApiRequest(_registerUser, 'Usuarios: Registrar')

   // ------------- Edit Users ---------------
   async function _editUser() {
      const payload = {
         id: formData.id,
         documentType: formData.documentType,
         documentNumber: formData.documentNumber,
         name: formData.name,
         cellphone: formData.cellphone,
         roles: formData.roles?.map(role => role?.id) ?? [],
         groups: formData.groups?.map(groups => groups?.id) ?? [],
         entityNit: formData.entityNit,
         entityName: formData.entityName,
         status: formData?.globalStatus,
         adminUser: formData.adminUser,
         email: formData?.email,
         thirdPartyCode: formData?.thirdPartyCode?.code
      }
      if (formData.documentType !== 'NI') {
         payload.lastName = formData.lastName
      }
      if (formData.telephone) {
         payload.telephone = formData.telephone
      }
      if (isInternalAdmin && formData.userType === 'I') {
         payload.adminUser = formData.adminUser
      }
      const url = `${process.env.REACT_APP_USERS_API}/users?ip=${ipDevice}`
      const editedUser = await putToApi(url, payload)
      setTableData({
         ...VALUES.TABLE_DATA_INTIAL_STATE,
         data: [editedUser]
      })
      cleanForm()
      setFormBehavior(isSearch ? VALUES.FORM_BEHAVIOR.SEARCH : VALUES.FORM_BEHAVIOR.REGISTER)
      const userName = `${editedUser.name} ${editedUser.lastName ? editedUser.lastName : ''}`
      dispatch(openSnackBarAction(`Se actualizó correctamente el Usuario ${userName}`, 'info'))
   }

   const editUser = useApiRequest(_editUser, 'Usuarios: Editar')

   const _saveUserGroups = async (user, formDataGroups = [], thirdPartyCode) => {
      const userId = user?.id
      for (let i = 0; i < formDataGroups.length; i++) {
         const url = `${process.env.REACT_APP_GROUP}/groups/${formDataGroups[i]?.id}/assign`
         await postToApi(url, { usersId: [userId], thirdPartyCode })
      }
   }

   const saveUserGroups = useApiRequest(_saveUserGroups, 'Grupos: Asignar grupo a usuario')

   // -------------- Form Actions ---------------
   async function actionSubmit(type) {
      setPopoverProp(false)
      if (type === VALUES.FORM_BEHAVIOR.REGISTER) {
         const user = await registerUser()
         if (user) {
            await saveUserGroups(user, formData.groups, formData?.thirdPartyCode?.code)
            setTableData(prev => ({
               ...prev,
               data: [
                  {
                     ...user,
                     thirdParty: {
                        status: 'A',
                        ...find(thirdTypes, { code: formData?.thirdPartyCode?.code }),
                        roleList: formData?.roles ?? [],
                        rolesByGroup: formData?.groups?.map(group => ({
                           group,
                           roleList: group?.roles
                        }))
                     },
                     thirdPartyCode: formData?.thirdPartyCode?.code ?? ''
                  }
               ]
            }))
         }
      }
      if (type === VALUES.FORM_BEHAVIOR.EDIT) {
         editUser()
      }
      if (type === VALUES.FORM_BEHAVIOR.SEARCH) {
         searchUsers(formData, VALUES.PAGING_ORDER_INITIAL_STATE)
         handleSelectUserAudit()
      }
   }

   function actionEdit(rowData) {
      setFormBehavior(VALUES.FORM_BEHAVIOR.EDIT)
      setFormData({
         id: rowData.id,
         name: rowData.name,
         lastName: rowData.lastName,
         documentNumber: rowData.documentNumber,
         documentType: rowData.documentType,
         cellphone: rowData.cellphone,
         email: rowData.email,
         telephone: rowData.telephone,
         status: rowData.thirdParty?.status,
         globalStatus: rowData.status,
         roles: rowData?.thirdParty?.roleList ?? [],
         groups: map(rowData?.thirdParty?.rolesByGroup, 'group.id').map(id => find(groups, { id })),
         entityNit: rowData.entityNit,
         entityName: rowData.entityName,
         adminUser: rowData.adminUser,
         userType: rowData.userType,
         userEntity: {
            name: rowData.entityName ?? '',
            nit: rowData.entityNit ?? ''
         },
         thirdPartyCode: rowData?.thirdParty
      })
      setPopoverProp(true)
   }

   async function _onApplyRolesToUserCallback(userId, selectedRoles, rowData) {
      const roles = selectedRoles.map(selectedRol => selectedRol.id)
      const thirdPartyCode = rowData?.thirdParty?.code
      const payload = { roles, thirdPartyCode }
      const url = `${process.env.REACT_APP_USERS_API}/users/${userId}/roles`
      const appliedRoles = await postToApi(url, payload)
      const dataWithUpdatedRoles = []
      let dni
      tableData.data.forEach(user => {
         if (user.id === userId && user?.thirdParty?.code === thirdPartyCode) {
            user.thirdParty = { ...user.thirdParty, roleList: appliedRoles }
            dni = user.documentNumber
         }
         dataWithUpdatedRoles.push(user)
      })
      setTableData({ ...tableData, data: dataWithUpdatedRoles })
      dispatch(openSnackBarAction(`Se actualizaron los roles correctamente para el usuario con DNI ${dni}`, 'info'))
   }

   const onApplyRolesToUserCallback = useApiRequest(_onApplyRolesToUserCallback, 'Usuarios: Aplicar Roles')

   const parametersOfSelectAndApply = {
      availableRoles,
      onApplyRolesToUserCallback
   }

   // -------------- Obtener el valor IP del device ---------------
   function getIpDevice() {
      dispatch(openSpinnerAction())
      fetch('https://api.ipify.org/?format=json')
         .then(response => response.json())
         .then(data => {
            const ip = data.ip
            const ipValue = ip.replace(/\./g, '')
            setIpDevice(ipValue)
            dispatch(closeSpinnerAction())
         })
         .catch(err => {
            dispatch(closeSpinnerAction())
            dispatch(openSnackBarAction(`${handleErrorMessage(err)} al obtener el valor IP`))
         })
   }

   const _getAPIGroups = async () => {
      const url = `${process.env.REACT_APP_USERS_API}/users/findAllowedGroupsToAssign`
      const resp = await getFromApi(url)
      if (resp.length) setGroups(resp)
   }

   const _getThirdTypes = async () => {
      const endpoint = isInternalUser ? '/validation/users/third-party-codes' : '/users/third-party/types'
      const url = `${process.env.REACT_APP_USERS_API}${endpoint}`
      const resp = await getFromApi(url)
      const thirdTypes = sort(
         reject(resp, opt => ['00', '02'].includes(opt?.code)),
         ['description']
      )
      if (thirdTypes.length) setThirdTypes(thirdTypes)
   }

   const _updateStatus = async (status, rowData, row) => {
      const userId = rowData?.id
      const thirdPartyId = rowData?.thirdParty?.id
      const url = `${process.env.REACT_APP_USERS_API}/users/update-status`
      await putToApi(url, { userId, thirdPartyId, status })
      searchUsers(searchFilters, paginationParams)
      dispatch(
         openSnackBarAction(
            `Se actualizó correctamente el usuario ${rowData?.thirdParty?.description}: ${rowData?.documentNumber}`,
            'success'
         )
      )
   }

   const getAPIGroups = useApiRequest(_getAPIGroups, 'Usuarios: Fetch Users')
   const getThirdTypes = useApiRequest(_getThirdTypes, 'Usuarios: Obtener tipos de terceros')
   const updateUserStatus = useApiRequest(_updateStatus, 'Usuarios: Actualizar estado')

   const actions = [
      seeLogPermission && {
         icon: 'visibility',
         tooltip: 'Ver Traza',
         onClick: (_, rowData) => handleSelectUserAudit(rowData)
      },
      editPermission && {
         icon: 'edit',
         tooltip: 'Editar fila',
         onClick: (_, rowData) => actionEdit(rowData)
      }
   ]

   useEffect(() => {
      getIpDevice()
      setOptions(prev => [...prev, ...documentTypes])
      getAvailableRoles()
      getAPIGroups()
      getThirdTypes()
   }, [])

   useEffect(() => {
      clean()
   }, [isSearch])

   return (
      <SecurityAdministrationLayout
         title={TITLE[formBehavior]}
         form={
            <UserForm
               key={renewForm}
               formData={formData}
               setForm={setFormData}
               documentTypes={documentTypes}
               actionSubmit={actionSubmit}
               formBehavior={formBehavior}
               cleanCallback={cleanForm}
               availableRoles={availableRoles}
               groups={groups}
               thirdTypes={thirdTypes}
               fetchEntitiesByName={fetchEntitiesByName}
               testDocNumberValidation={testDocLengthValidation}
            />
         }
         table={
            <>
               <div style={{ display: logUserSelected ? 'none' : 'block' }}>
                  <UserTable
                     isSearch={isSearch}
                     order={paginationParams.order}
                     users={tableData}
                     searchUsers={searchUsers}
                     actionEdit={actionEdit}
                     customActions={actions}
                     cleanCallback={clean}
                     onStatusChange={updateUserStatus}
                     parametersOfSelectAndApply={parametersOfSelectAndApply}
                     isInternalAdmin={isInternalAdmin}
                     isInternalSuperAdmin={isInternalSuperAdmin}
                     docTypes={docTypeList}
                  />
               </div>
               {logUserSelected && <LogTable userId={logUserSelected} onClose={handleSelectUserAudit} />}
            </>
         }
         popoverProp={isShowingPopover}
         setPopoverProp={setPopoverProp}
         searchFilters={getFiltersDescriptors(searchFilters)}
         onRemoveFilter={removeSearchFilter}
      />
   )
}

export default User
